import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import MovieCard from '@/components/feature/MovieCard';
import { categories, movies } from '@/mocks/movies';

function CategorySchema(cat: { name: string; description: string } | undefined) {
  if (!cat) return null;
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${cat.name} - 91精品影视`,
    description: cat.description,
    url: `https://www.91jpys.com/category/${cat.name}`,
  };
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSubcategory, setActiveSubcategory] = useState<string>('全部');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as const });
  }, [slug]);

  useEffect(() => {
    setActiveSubcategory('全部');
  }, [slug]);

  const category = categories.find((c) => c.slug === slug);

  const filteredMovies = movies.filter((m) => {
    if (m.category !== category?.id) return false;
    if (activeSubcategory === '全部') return true;
    return m.subcategory === activeSubcategory;
  });

  if (!category) {
    return (
      <>
        <Navbar />
        <div className="pt-24 pb-20 text-center px-4">
          <h1 className="text-2xl font-heading font-bold text-foreground-900">分类未找到</h1>
          <Link to="/" className="text-primary-500 hover:text-primary-600 mt-4 inline-block cursor-pointer">返回首页</Link>
        </div>
        <Footer />
      </>
    );
  }

  const subcategoryOptions = ['全部', ...category.subcategories];

  return (
    <>
      {CategorySchema({ name: category.name, description: `浏览${category.name}分类下的所有精彩影视内容` })}
      <Navbar />

      <main>
        <div className="pt-20 pb-6 px-4 md:px-6 mx-auto" style={{ maxWidth: '1400px' }}>
          <Breadcrumb
            items={[
              { name: '首页', path: '/' },
              { name: category.name },
            ]}
          />
        </div>

        <section className="px-4 md:px-6 pb-6 mx-auto" style={{ maxWidth: '1400px' }}>
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600">
              <i className={`${category.icon} text-xl`}></i>
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                {category.name}
              </h1>
              <p className="text-sm text-foreground-400 mt-0.5">
                共 {filteredMovies.length} 部作品
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-8">
            {subcategoryOptions.map((sub) => (
              <button
                key={sub}
                onClick={() => {
                  setActiveSubcategory(sub);
                }}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                  activeSubcategory === sub
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {sub}
              </button>
            ))}
          </div>

          {filteredMovies.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
              {filteredMovies.map((movie) => (
                <MovieCard
                  key={movie.id}
                  id={movie.id}
                  title={movie.title}
                  coverImage={movie.coverImage}
                  rating={movie.rating}
                  year={movie.year}
                  subcategory={movie.subcategory}
                  category={movie.category}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-300">
                <i className="ri-inbox-line text-2xl"></i>
              </div>
              <p className="text-foreground-400 text-sm">暂无相关内容</p>
            </div>
          )}
        </section>
      </main>

      <Footer />
      <ScrollToTop />
    </>
  );
}